package com.iptvplayer.shared.data.remote.api

import com.iptvplayer.shared.util.Constants
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

// ─────────────────────────── Wire models (snake_case) ───────────────────────────
// The server serializes with JsonNamingStrategy.SnakeCase, so every multi-word
// field carries an explicit @SerialName to match the wire format exactly.

/** The content a Watch Together room is playing. */
@Serializable
data class WatchRoomContent(
    @SerialName("stream_url") val streamUrl: String,
    @SerialName("content_id") val contentId: String,
    val name: String,
    val type: String, // ContentType name: CHANNEL / VOD / …
    val logo: String? = null,
)

@Serializable
data class CreateWatchRoomRequest(val content: WatchRoomContent)

@Serializable
data class CreateWatchRoomResponse(@SerialName("room_id") val roomId: String)

@Serializable
data class JoinWatchRoomResponse(
    val content: WatchRoomContent,
    val playing: Boolean = true,
    @SerialName("position_ms") val positionMs: Long = 0,
    val viewers: Int = 0,
    @SerialName("guest_token") val guestToken: String = "",
)

// ─────────────────────────── WebSocket messages ───────────────────────────
// Incoming frames are discriminated by their "type" field; [WatchWsMessage] is a
// permissive union — parse the frame into it and inspect [type].

@Serializable
data class WatchWsMessage(
    val type: String = "",
    // room_state
    val content: WatchRoomContent? = null,
    // room_state + sync
    val playing: Boolean? = null,
    @SerialName("position_ms") val positionMs: Long? = null,
    // room_state + presence
    val viewers: Int? = null,
    // welcome
    @SerialName("self_id") val selfId: String? = null,
    val peers: List<WatchPeer>? = null,
    // chat / peer_join / peer_leave
    val id: String? = null,
    val name: String? = null,
    val text: String? = null,
)

@Serializable
data class WatchPeer(val id: String, val name: String)

/** Outgoing: playback control (server is the authoritative timekeeper). */
@Serializable
data class WatchSetStateMsg(
    val playing: Boolean,
    @SerialName("position_ms") val positionMs: Long,
    val type: String = "set_state",
)

/** Outgoing: chat line. */
@Serializable
data class WatchChatOutMsg(
    val text: String,
    val type: String = "chat",
)

// ─────────────────────────── REST client ───────────────────────────

/**
 * Watch Together REST endpoints. Creating a room requires the signed-in user's
 * bearer token (attached automatically by the shared client); joining is public —
 * possession of the room id from the share link is the capability.
 */
class WatchApi(private val client: HttpClient) {

    suspend fun createRoom(content: WatchRoomContent): String =
        client.post("/api/watch") {
            contentType(ContentType.Application.Json)
            setBody(CreateWatchRoomRequest(content))
        }.body<CreateWatchRoomResponse>().roomId

    suspend fun joinRoom(roomId: String): JoinWatchRoomResponse =
        client.post("/api/watch/$roomId/join").body()

    companion object {
        /** The share link guests open (web joins directly; native pastes it). */
        fun shareLink(roomId: String): String = "${Constants.BASE_URL}/#/watch/$roomId"

        /** ws(s):// URL for the room's WebSocket. */
        fun wsUrl(roomId: String, token: String?, displayName: String): String {
            val base = Constants.BASE_URL
                .replaceFirst("https://", "wss://")
                .replaceFirst("http://", "ws://")
            val name = displayName.encodeURLParameter()
            val tokenPart = token?.let { "&token=${it.encodeURLParameter()}" } ?: ""
            return "$base/api/watch/$roomId/ws?name=$name$tokenPart"
        }

        /** Server tee-relay URL for LIVE rooms (single upstream, fanned out). */
        fun liveRelayUrl(roomId: String, token: String): String =
            "${Constants.BASE_URL}/api/watch/$roomId/stream?token=${token.encodeURLParameter()}"

        /** Extract a room id from a pasted share link or a bare room code. */
        fun parseRoomId(input: String): String? {
            val trimmed = input.trim()
            if (trimmed.isEmpty()) return null
            Regex("#/watch/([A-Za-z0-9]+)").find(trimmed)?.let { return it.groupValues[1] }
            return trimmed.takeIf { it.matches(Regex("[A-Za-z0-9]{6,32}")) }
        }
    }
}
